import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'

import { H3 } from '@driftt/tide-core'

import ActivityTimeline from 'components/ActivityTimeline'
import Card from 'components/Card'
import { DateRange } from 'components/DateRangePicker'
import { driftEmptyStateBuilder } from 'components/States'
import { AccountDetailsPageParams } from 'hooks/useAccountCompanyTitle'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import CCRMFeed from 'sections/CCRM/Feed'
import { getEnabledIntegrations } from 'selectors'
import { formatRangeToFullDate } from 'utils/formatDates'

import AccountActivityStats from '../AccountActivityStats'
import ActivitiesFeed from '../ActivitiesFeed'
import CompanyIntelSection from '../CompanyIntelSection'
import EngagementScoreSection from '../EngagementScoreSection'
import SalesforceSection from '../SalesforceSection'

import styles from './styles.module.css'

interface FeedTabProps {
  sessionMetrics: SessionMetrics
  salesforceData?: any
  companyData?: any
  explainData?: any
}

const FeedTab = ({ sessionMetrics, salesforceData, companyData, explainData }: FeedTabProps) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined)
  const { id: accountId } = useParams<AccountDetailsPageParams>()
  const { search } = useLocation()
  const hasSalesForce = useSelector(getEnabledIntegrations).salesforce
  const newFeed = search.includes('feedV2') && hasSalesForce

  const EmptyComponent = useMemo(
    () =>
      driftEmptyStateBuilder(
        'No account activity yet',
        'Once your account visits your site, it will show up here.'
      ),
    []
  )

  return (
    <>
      <div className={styles.leftSide}>
        <div className={styles.feedContainer}>
          <div>
            <Card>
              <H3 className={styles.activityTimelineHeader}>Activity timeline</H3>
              <ActivityTimeline
                dateRange={dateRange}
                setDateRange={setDateRange}
                accountId={accountId}
                accountName={companyData?.name}
                useExternalData={newFeed}
              />
            </Card>
            {dateRange && (
              <div className={styles.timelineDateContainer}>
                <div className={styles.timelineDateBorder} />
                <div className={styles.timelineDate}>
                  {formatRangeToFullDate(dateRange)}
                  <span
                    className={styles.timelineDateClear}
                    onClick={() => setDateRange(undefined)}>
                    Jump to most recent
                  </span>
                </div>
              </div>
            )}
          </div>
          {newFeed ? (
            <ActivitiesFeed accountId={accountId} dateRange={dateRange} />
          ) : (
            <CCRMFeed
              accountId={accountId}
              scrollTargetId="accountDetailsPage"
              includeAllTimeActivities
              sessionMetrics={sessionMetrics}
              includeSortControl={false}
              withFilters={false}
              emptyComponent={EmptyComponent}
              dateRange={dateRange}
            />
          )}
        </div>
      </div>
      <div className={styles.rightSide}>
        {companyData && <CompanyIntelSection company={companyData} />}

        <AccountActivityStats accountId={accountId} />

        {explainData && <EngagementScoreSection explainData={explainData} />}
        {salesforceData && (
          <SalesforceSection
            baseUrl={salesforceData.baseUrl}
            accounts={salesforceData.accounts}
            opportunities={salesforceData.opportunities}
          />
        )}
      </div>
    </>
  )
}

export default FeedTab
